import { createApi } from "@reduxjs/toolkit/query/react";
import { CITY, TagId, TagType } from "../../configs";
import {
  City,
  CityDetails,
  HttpMethod,
  IWriteCity,
  PaginatedResponse,
  SearchByDisplayName,
} from "../../types";
import { createBaseQuery } from "../helpers/baseQueryHelper";

const cityApi = createApi({
  reducerPath: CITY.reducerPath,
  baseQuery: createBaseQuery(CITY.baseUrl),
  tagTypes: CITY.tagTypes,
  endpoints(builder) {
    return {
      fetchAllCities: builder.query<City[], void>({
        query: () => ({
          url: "/all",
          method: HttpMethod.GET,
        }),
        providesTags: () => [{ type: TagType.City, id: TagId.ALL_LIST }],
      }),

      toggleCityIsActiveStatus: builder.mutation<
        void,
        { cityId: string; isActive: boolean }
      >({
        query: ({ cityId, isActive }) => ({
          url: `/${cityId}/active`,
          method: HttpMethod.PATCH,
          params: { isActive },
        }),
        invalidatesTags: () => [{ type: TagType.City, id: TagId.LIST }],
      }),

      fetchCities: builder.query<PaginatedResponse<City>, SearchByDisplayName>({
        query: (searchQuery) => ({
          url: "/",
          method: HttpMethod.GET,
          params: searchQuery,
        }),
        providesTags: () => [{ type: TagType.City, id: TagId.LIST }],
      }),

      fetchCityDetails: builder.query<CityDetails, string>({
        query: (cityId) => ({
          url: `/${cityId}`,
          method: HttpMethod.GET,
        }),
        providesTags: (_result, _error, cityId) => [
          { type: TagType.City, id: cityId },
        ],
      }),

      createCity: builder.mutation<CityDetails, IWriteCity>({
        query: (body) => ({
          url: "",
          method: HttpMethod.POST,
          body,
        }),
        invalidatesTags: () => [
          { type: TagType.City, id: TagId.ALL_LIST },
          { type: TagType.City, id: TagId.LIST },
        ],
      }),

      updateCity: builder.mutation<void, { cityId: string; body: IWriteCity }>({
        query: ({ cityId, body }) => ({
          url: `/${cityId}`,
          method: HttpMethod.PUT,
          body,
        }),
        invalidatesTags: (_result, _error, { cityId }) => [
          { type: TagType.City, id: TagId.ALL_LIST },
          { type: TagType.City, id: TagId.LIST },
          { type: TagType.City, id: cityId },
        ],
      }),

      deleteCity: builder.mutation<void, string>({
        query: (cityId) => ({
          url: `/${cityId}`,
          method: HttpMethod.DELETE,
        }),
        invalidatesTags: (_result, _error, cityId) => [
          { type: TagType.City, id: TagId.ALL_LIST },
          { type: TagType.City, id: TagId.LIST },
          { type: TagType.City, id: cityId },
        ],
      }),
    };
  },
});

export const {
  useFetchAllCitiesQuery,
  useFetchCitiesQuery,
  useFetchCityDetailsQuery,
  useCreateCityMutation,
  useUpdateCityMutation,
  useToggleCityIsActiveStatusMutation,
  useDeleteCityMutation,
} = cityApi;

export { cityApi };
